import { observer } from 'mobx-react'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useParams, useSearchParams } from 'react-router-dom'

import { MagicLinkChooser } from '../../../components/auth/MagicLinkChooser'
import { logBreadcrumb, logError } from '../../../services/errorHandler'
import { isAndroid } from '../../../utils/device'
import { toSafeExternalUrl } from '../../../utils/url/safe-external-url'
import {
    buildAppLinkUrl,
    buildContinueOnWebUrl,
} from '../../../utils/auth/deep-link'

export const LoginCoacheePage: React.FC = observer(() => {
    const { i18n } = useTranslation()
    const [search] = useSearchParams()
    const { pathname } = useLocation()
    const { companyId, ssoId } = useParams<{
        companyId: string
        ssoId: string
    }>()
    // Presence of the param — not its value — determines one-time mode, which
    // matches the chooser's documented contract. An empty `?magicToken=` still
    // counts as a magic-token landing and must lock the one-time flow.
    const hasMagicTokenParam = search.has('magicToken')
    const isOneTimeMagicTokenLink = hasMagicTokenParam
    const language = i18n.resolvedLanguage
    // i18n.resolvedLanguage can be a region-qualified locale ("en-GB",
    // "fr-CA"); detect French explicitly so anything non-French (incl.
    // en-GB / en-US) gets the English store URL.
    const isFrench = language?.toLowerCase().startsWith('fr') ?? false

    const playStoreAppLink = isFrench
        ? import.meta.env.VITE_APP_FR_PLAY_STORE_URL
        : import.meta.env.VITE_APP_EN_PLAY_STORE_URL

    const appStoreAppLink = isFrench
        ? import.meta.env.VITE_APP_FR_APP_STORE_URL
        : import.meta.env.VITE_APP_EN_APP_STORE_URL

    // do_login=true means coachee-web bounced us back (rare) — forward straight
    // to the per-route web URL. app_attempt=1 means an "Open in app" handoff
    // came back to us: the OS didn't capture the Universal/App Link (app absent,
    // or blocked by MAM / an in-app browser), so we fall back to the platform
    // store — the same outcome the old custom-scheme timer produced, now
    // signalled deterministically by the round-trip rather than a timeout.
    const directForward = search.has('do_login')
    const appAttemptFailed = search.has('app_attempt') && !directForward

    // Store URL for the current platform, protocol-checked. null when the env
    // URL is missing/invalid — we then skip the redirect and fall back to the
    // chooser rather than navigating somewhere broken.
    const storeUrl = isAndroid() ? playStoreAppLink : appStoreAppLink
    const safeStoreUrl = toSafeExternalUrl(storeUrl)

    // Track whether a short-circuit redirect couldn't fire (env misconfig — no
    // per-route mapping, or no valid store URL) so we degrade to the chooser
    // instead of a blank page, and log to Sentry so the misconfig is visible.
    const [redirectFailed, setRedirectFailed] = useState(false)

    const getContinueOnWebUrl = useCallback(
        () => buildContinueOnWebUrl(pathname, search, { companyId, ssoId }),
        [companyId, pathname, search, ssoId],
    )

    useEffect(() => {
        if (directForward) {
            const url = getContinueOnWebUrl()
            if (url) {
                window.location.replace(url)
            } else {
                logError(
                    new Error(
                        'LoginCoacheePage: directForward had no URL to redirect to',
                    ),
                    { pathname },
                )
                setRedirectFailed(true)
            }
            return
        }

        if (appAttemptFailed) {
            logBreadcrumb('app_store_fallback_fired')
            if (safeStoreUrl) {
                window.location.replace(safeStoreUrl)
            } else {
                logError(
                    new Error(
                        'LoginCoacheePage: app_attempt re-entry had no valid store URL',
                    ),
                    { pathname },
                )
                setRedirectFailed(true)
            }
        }
    }, [
        appAttemptFailed,
        directForward,
        getContinueOnWebUrl,
        pathname,
        safeStoreUrl,
    ])

    const openInAppUrl = buildAppLinkUrl(pathname, search, { companyId, ssoId })

    const handleContinueOnWeb = useCallback(() => {
        const url = getContinueOnWebUrl()
        if (!url) {
            logError(
                new Error(
                    'LoginCoacheePage: continueOnWeb had no URL to redirect to',
                ),
                { pathname },
            )
            return
        }
        window.location.href = url
    }, [getContinueOnWebUrl, pathname])

    if ((directForward || appAttemptFailed) && !redirectFailed) {
        // Render nothing while the useEffect fires the redirect. If it couldn't
        // fire (env misconfig — see useEffect above) we fall through to the
        // chooser as a degraded fallback.
        return null
    }

    return (
        <MagicLinkChooser
            openInAppUrl={openInAppUrl}
            onContinueOnWeb={handleContinueOnWeb}
            appStoreUrl={appStoreAppLink}
            playStoreUrl={playStoreAppLink}
            isOneTimeMagicTokenLink={isOneTimeMagicTokenLink}
        />
    )
})

export default LoginCoacheePage
